// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the colour controls at the top of the Tools panel's View tab:
 * the palette, an "Invert palette" switch, and the colour limits.
 *
 *   Colours
 *   [▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬]   ← the bar as the globe draws it, with ticks
 *   Palette   [ Original      ▾]
 *   [✓] Invert palette
 *   Colour limits   [ 0.5 ] to [ 120 ] µg m-3
 *   [ Reset colours ]
 *
 * One list of palettes, with inversion as a switch rather than a second,
 * "(reversed)" copy of every entry (AYNI 2D lists both). The limits are
 * physical values, typed: the low one takes the palette's first colour,
 * the high one its last, and values beyond are clamped to those colours
 * (the stretch of `colorScaleDisplay`, on a log axis for a log release).
 *
 * Works on what carries values: a value-encoded release or an older
 * data-encoded row. A picture keeps its colours and gets one line saying
 * so; a classified release keeps its class colours, listed as the
 * floating legend lists them.
 *
 * Every change goes straight to the globe (`setDisplay`): a palette costs
 * one LUT upload, which is why there is no Apply button. Renders into
 * `#tools-view-palette`; the controls are rebuilt only when the dataset's
 * scale changes, so typing in a limit is never interrupted.
 */

import { t, tAttr, tHtml, type MessageKey } from '../i18n'
import {
  DEFAULT_DISPLAY,
  PALETTE_IDS,
  displayGradientStops,
  isAtDefault,
  isDefaultDisplay,
  valueAtPosition,
  type ColorScaleDisplay,
  type PaletteId,
} from '../services/colorScaleDisplay'
import { releaseOf, type ColorScale } from '../types/color-scale'
import type { DisplayGroup } from '../services/displayGroups'
import { renderClassLegend } from './classLegendUI'
import { renderColorbar } from './colorbarUI'
import { withPhaseBars } from './phaseLegendUI'
import { escapeAttr, escapeHtml } from './domUtils'
import type { ReferenceLine } from '../services/referenceLines'

export interface ViewPaletteDeps {
  /** The scale of the dataset on the primary globe, or null when it carries no values. */
  scale(): ColorScale | null
  display(): ColorScaleDisplay
  setDisplay(next: ColorScaleDisplay): void
  /** AYNI: what "Reset colours" returns to — the dataset's display group, else its own colours. */
  defaultDisplay?(): ColorScaleDisplay
  /** AYNI: the shared colour standard the dataset follows, for a line saying so. */
  group?(): DisplayGroup | null | undefined
  /**
   * AYNI (owner, 2026-10-14): the reference lines' switch
   * (`referenceLines.ts`), shown when the dataset has any; `lines` are the
   * ones the preview marks (none while hidden).
   */
  references?: {
    has(): boolean
    on(): boolean
    set(on: boolean): void
    lines(): readonly ReferenceLine[]
  }
  announce?(message: string): void
}

export interface ViewPaletteHandle {
  /** Re-read the dataset and the display (a dataset loaded, or the display changed elsewhere); `force` redraws even when neither changed. */
  refresh(force?: boolean): void
}

/** A limit as typed into a number field: three significant figures, no grouping. */
function limitText(value: number): string {
  if (!Number.isFinite(value)) return ''
  return String(Number(value.toPrecision(3)))
}

/** The line under the bar for a dataset in a shared standard, in this interface's words. */
function groupNote(group: DisplayGroup): string {
  const key = `tools.palette.groupName.${group.id}` as MessageKey
  const name = t(key)
  return name === key ? t('tools.palette.groupOther') : t('tools.palette.group', { group: name })
}

function swatch(scale: ColorScale, palette: PaletteId, inverted: boolean): string {
  const stops = displayGradientStops(scale, { ...DEFAULT_DISPLAY, palette, inverted }, 12)
    .map((s) => `rgb(${s.rgba[0]}, ${s.rgba[1]}, ${s.rgba[2]}) ${(s.position * 100).toFixed(0)}%`)
    .join(', ')
  return `linear-gradient(to right, ${stops})`
}

export function mountViewPalette(host: HTMLElement, deps: ViewPaletteDeps): ViewPaletteHandle {
  /** The scale the controls were built for; a different one rebuilds them. */
  let builtFor: ColorScale | null | undefined

  function apply(next: ColorScaleDisplay): void {
    deps.setDisplay(next)
    drawn = deps.display()
    update()
  }

  /** Bring the preview, the swatch, the switch and the limits in line with the display. */
  function update(): void {
    const scale = builtFor
    if (!scale) return
    const display = deps.display()
    const preview = host.querySelector('.tools-palette-preview')
    if (preview) {
      // AYNI: a bar per palette for a release with a class map (IMERG's rain/snow).
      const bar = withPhaseBars(renderColorbar({ scale, display, references: deps.references?.lines() }), scale, display)
      bar.classList.add('tools-palette-preview', 'colorbar-controls-preview')
      preview.replaceWith(bar)
    }
    const select = host.querySelector<HTMLSelectElement>('#tools-palette-select')
    if (select) {
      select.value = display.palette
      select.style.setProperty('--swatch', swatch(scale, display.palette, !!display.inverted))
    }
    const invert = host.querySelector<HTMLButtonElement>('#tools-palette-invert')
    if (invert) {
      invert.classList.toggle('active', !!display.inverted)
      invert.setAttribute('aria-pressed', String(!!display.inverted))
    }
    const refs = host.querySelector<HTMLButtonElement>('#tools-palette-refs')
    if (refs) {
      const on = !!deps.references?.on()
      refs.hidden = !deps.references?.has()
      refs.classList.toggle('active', on)
      refs.setAttribute('aria-pressed', String(on))
    }
    const lo = host.querySelector<HTMLInputElement>('#tools-palette-min')
    const hi = host.querySelector<HTMLInputElement>('#tools-palette-max')
    // Not under the viewer's cursor: only fields they are not editing.
    if (lo && document.activeElement !== lo) lo.value = limitText(valueAtPosition(scale, display, 0))
    if (hi && document.activeElement !== hi) hi.value = limitText(valueAtPosition(scale, display, 1))
    const reset = host.querySelector<HTMLButtonElement>('#tools-palette-reset')
    if (reset) reset.disabled = deps.defaultDisplay ? isAtDefault(display, deps.defaultDisplay()) : isDefaultDisplay(display)
    const note = host.querySelector<HTMLElement>('.tools-palette-group')
    if (note) {
      const group = deps.group?.()
      note.hidden = !group
      note.textContent = group ? groupNote(group) : ''
    }
  }

  function build(scale: ColorScale | null): void {
    builtFor = scale
    const title = `<h4 class="tools-menu-section-title">${tHtml('tools.palette.title')}</h4>`
    if (!scale) {
      host.innerHTML = `${title}<p class="tools-menu-note">${tHtml('tools.palette.none')}</p>`
      return
    }
    if (releaseOf(scale)?.kind === 'luma8-classified') {
      host.innerHTML = `${title}<p class="tools-menu-note">${tHtml('tools.palette.classes')}</p>`
      // The same list the floating legend shows.
      const classes = renderClassLegend({ scale })
      if (classes) {
        classes.classList.add('tools-palette-preview', 'colorbar-controls-preview')
        host.querySelector('.tools-menu-note')?.before(classes)
      }
      return
    }
    const units = scale.units ? `<span class="tools-palette-units">${escapeHtml(scale.units)}</span>` : ''
    host.innerHTML = `
      ${title}
      <div class="tools-palette-preview"></div>
      <p class="tools-menu-note tools-palette-group" hidden></p>
      <div class="tools-menu-language-row tools-palette-row">
        <label for="tools-palette-select" class="tools-menu-layers-label">${tHtml('colorbar.palette.label')}</label>
        <select id="tools-palette-select" class="tools-menu-language-select tools-palette-select">
          ${PALETTE_IDS.map((id) => `<option value="${escapeAttr(id)}">${tHtml(`colorbar.palette.${id}` as MessageKey)}</option>`).join('')}
        </select>
      </div>
      <div class="tools-palette-switches">
        <button type="button" class="tools-menu-item" id="tools-palette-invert" aria-pressed="false">
          <span class="tools-menu-item-check" aria-hidden="true"></span>
          <span class="tools-menu-item-label">${tHtml('tools.palette.invert')}</span>
        </button>
        <button type="button" class="tools-menu-item" id="tools-palette-refs" aria-pressed="false" title="${tAttr('reference.toggleHint')}" hidden>
          <span class="tools-menu-item-check" aria-hidden="true"></span>
          <span class="tools-menu-item-label">${tHtml('reference.toggle')}</span>
        </button>
      </div>
      <div class="tools-palette-limits" role="group" aria-label="${tAttr('tools.palette.limits')}">
        <span class="tools-menu-layers-label">${tHtml('tools.palette.limits')}</span>
        <div class="tools-palette-limits-row">
          <input type="number" id="tools-palette-min" class="tools-palette-limit" step="any" inputmode="decimal" aria-label="${tAttr('tools.palette.min')}">
          <span class="tools-palette-to">${tHtml('tools.palette.to')}</span>
          <input type="number" id="tools-palette-max" class="tools-palette-limit" step="any" inputmode="decimal" aria-label="${tAttr('tools.palette.max')}">
          ${units}
        </div>
        <p class="tools-menu-note">${tHtml('tools.palette.limitsHint')}</p>
      </div>
      <button type="button" class="tools-measure-btn tools-palette-reset" id="tools-palette-reset">${tHtml('colorbar.reset')}</button>
      <p class="tools-menu-note">${tHtml('tools.palette.saved')}</p>`

    host.querySelector<HTMLSelectElement>('#tools-palette-select')?.addEventListener('change', (e) => {
      const palette = (e.target as HTMLSelectElement).value as PaletteId
      apply({ ...deps.display(), palette })
      deps.announce?.(t('tools.palette.announce', { label: t(`colorbar.palette.${palette}` as MessageKey) }))
    })
    host.querySelector<HTMLButtonElement>('#tools-palette-invert')?.addEventListener('click', () => {
      const inverted = !deps.display().inverted
      apply({ ...deps.display(), inverted })
      deps.announce?.(t(inverted ? 'tools.palette.inverted.on' : 'tools.palette.inverted.off'))
    })
    host.querySelector<HTMLButtonElement>('#tools-palette-refs')?.addEventListener('click', () => {
      const on = !deps.references?.on()
      deps.references?.set(on)
      deps.announce?.(t(on ? 'reference.shown' : 'reference.hidden'))
    })
    const commitLimits = (): void => {
      const lo = Number(host.querySelector<HTMLInputElement>('#tools-palette-min')?.value)
      const hi = Number(host.querySelector<HTMLInputElement>('#tools-palette-max')?.value)
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || !(hi > lo)) {
        // Refused, and the live limits put back: a reversed range would
        // draw one flat colour and look like a fault elsewhere.
        deps.announce?.(t('tools.palette.invalid'))
        const active = document.activeElement as HTMLElement | null
        active?.blur()
        update()
        return
      }
      // AYNI: kept as values on their own axis (the shared standard's, else the
      // stream's), so they may pass the encoded range and are saved as typed.
      const now = deps.display()
      const axis = now.limits?.scale ?? (releaseOf(scale)?.kind === 'luma8-log' ? 'log' : 'linear')
      if (axis === 'log' && !(lo > 0)) {
        deps.announce?.(t('tools.palette.invalid'))
        update()
        return
      }
      apply({ ...now, stretch: { lo: 0, hi: 1 }, limits: { min: lo, max: hi, scale: axis } })
    }
    for (const id of ['#tools-palette-min', '#tools-palette-max']) {
      const input = host.querySelector<HTMLInputElement>(id)
      input?.addEventListener('change', commitLimits)
      input?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') input.blur()
      })
    }
    host.querySelector<HTMLButtonElement>('#tools-palette-reset')?.addEventListener('click', () => {
      apply(deps.defaultDisplay ? deps.defaultDisplay() : { ...DEFAULT_DISPLAY })
      deps.announce?.(t('tools.palette.resetDone'))
    })
    update()
  }

  /** The display last drawn; displays are replaced, never mutated, so identity says "changed". */
  let drawn: ColorScaleDisplay | null = null

  /** Cheap when nothing changed, so the host may call it on a timer; `force` redraws what the display alone does not decide (the reference lines). */
  function refresh(force = false): void {
    const scale = deps.scale()
    const display = deps.display()
    if (!force && scale === builtFor && display === drawn) return
    drawn = display
    if (scale !== builtFor) build(scale)
    else update()
  }

  refresh()
  return { refresh }
}
